import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, CalendarDays, TrendingUp, UserRound, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { courseApi, registrationApi, tutorApi } from '@/services/api';

type DashboardCounts = {
  courses: number;
  registrations: number;
  tutors: number;
  convertedUsers: number;
};

export const AdminDashboard = () => {
  const [counts, setCounts] = useState<DashboardCounts>({
    courses: 0,
    registrations: 0,
    tutors: 0,
    convertedUsers: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const fetchCounts = async () => {
      try {
        setIsLoading(true);
        const [courses, tutors, registrations] = await Promise.all([
          courseApi.getAllCourses(controller.signal),
          tutorApi.getAllTutors({ signal: controller.signal, scope: 'management' }),
          registrationApi.getAll({ signal: controller.signal }),
        ]);

        if (controller.signal.aborted) {
          return;
        }

        setCounts({
          courses: courses.length,
          registrations: registrations.length,
          tutors: tutors.length,
          convertedUsers: registrations.filter((record) => record.status === 'Converted').length,
        });
        setStatsError('');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          setStatsError((error as Error).message || 'Failed to load dashboard counts.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void fetchCounts();
    return () => controller.abort();
  }, []);

  const stats = useMemo(
    () => [
      {
        label: 'Course Count',
        value: counts.courses.toLocaleString(),
        helper: 'Total courses',
        icon: BookOpen,
        tone: 'bg-blue-50 text-blue-700',
      },
      {
        label: 'User Register Count',
        value: counts.registrations.toLocaleString(),
        helper: 'All registration records',
        icon: Users,
        tone: 'bg-violet-50 text-violet-700',
      },
      {
        label: 'Tutor Count',
        value: counts.tutors.toLocaleString(),
        helper: 'Total tutor profiles',
        icon: UserRound,
        tone: 'bg-emerald-50 text-emerald-700',
      },
      {
        label: 'Converted User Count',
        value: counts.convertedUsers.toLocaleString(),
        helper: 'Converted registrations',
        icon: TrendingUp,
        tone: 'bg-amber-50 text-amber-700',
      },
    ],
    [counts]
  );

  const activity = [
    { action: 'Registration submitted', course: 'Course, sports, or event enquiry', time: 'Public workflow' },
    { action: 'Tutor profile updated', course: 'Tutor directory', time: 'Admin managed' },
    { action: 'Event listing updated', course: 'Unified event module', time: 'Dashboard controlled' },
  ];

  const quickActions = [
    { label: 'Create New Course', path: '/admin/courses/new' },
    { label: 'Review User Registrations', path: '/admin/registrations' },
    { label: 'Manage Tutor Profiles', path: '/admin/tutors' },
  ];

  return (
    <div className="space-y-8">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="page-heading">Admin Dashboard</h1>
        <p className="page-subtitle">Manage staff-owned content, tutor profiles, events, and registrations from one place.</p>
      </motion.div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card hover className="p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className={`rounded-lg p-3 ${stat.tone}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="text-2xl font-semibold tracking-tight text-slate-950">
                    {isLoading ? '...' : stat.value}
                  </span>
                </div>
                <p className="mt-4 text-sm font-semibold text-slate-600">{stat.label}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-400">{stat.helper}</p>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {statsError ? (
        <Card className="p-4">
          <p className="text-sm text-rose-600">{statsError}</p>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <div className="mb-5 flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-950">Recent Activity</h2>
            <CalendarDays className="h-5 w-5 text-slate-400" />
          </div>
          <div className="space-y-1">
            {activity.map((item) => (
              <div key={`${item.action}-${item.time}`} className="flex gap-3 border-b border-slate-100 py-3 last:border-b-0">
                <span className="mt-2 h-2 w-2 rounded-full bg-[rgb(var(--accent-solid))]" />
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{item.action}</p>
                  <p className="text-sm text-slate-600">{item.course}</p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-[0.14em] text-slate-400">{item.time}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="mb-5 text-lg font-semibold text-slate-950">Quick Actions</h2>
          <div className="space-y-3">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.path}
                className="block w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:border-slate-300 hover:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgb(var(--accent-solid))/0.14]"
              >
                <p className="font-semibold text-slate-900">{action.label}</p>
                <p className="mt-1 text-sm text-slate-600">Open the related admin workflow</p>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};
